import type { EPCR } from '@/types';
import { cn } from '@/lib/cn';
import { Truck, UserCheck, Users, Stethoscope } from 'lucide-react';
import { RuleTag } from '@/components/RuleTag';

/**
 * DispositionDimensionsPanel — EMSPCR Foundation v1.0 §2.16.
 *
 * NEMSIS v3.5.1 captures FOUR distinct disposition dimensions:
 *   - eDisposition.27 Unit Disposition          (Mandatory)
 *   - eDisposition.28 Patient Evaluation/Care
 *   - eDisposition.29 Crew Disposition
 *   - eDisposition.30 Transport Disposition
 *
 * They are NOT redundant. Per the Foundation they describe different
 * dimensions of the same event and must be asked cleanly — the
 * ImageTrend "three disposition menus on one screen" failure mode
 * is explicitly the anti-pattern we are avoiding.
 *
 * Rendered as four distinct cards with independent rule tags so the
 * crew knows exactly which standard cites each answer. Transport
 * decision lives downstream (existing DispositionTab big-button
 * bar); this panel covers the other three + a compact summary of
 * transport for cross-reference.
 */

interface Props {
  epcr: EPCR;
  onUpdate: (patch: Partial<EPCR>) => void;
}

const UNIT_DISPOSITIONS = [
  'Patient Treated, Transported',
  'Patient Treated, Transferred Care',
  'Patient Treated, Released (per protocol)',
  'Patient Refused Evaluation / Care',
  'Patient Refused Transport',
  'Cancelled Prior to Patient Contact',
  'No Patient Found',
  'Dead at Scene',
  'Patient Evaluated, No Treatment Required',
  'Standby - No Patient Contact',
];

const PATIENT_EVAL_CARE = [
  'Patient Evaluated and Care Provided',
  'Patient Evaluated, No Care Required',
  'Patient Refused Evaluation',
  'Patient Refused Care (Evaluation Completed)',
  'No Patient Contact',
  'Patient Dead on Arrival',
];

const CREW_DISPOSITIONS = [
  'Initiated and Continued Primary Care',
  'Initiated Primary Care and Transferred to Another EMS Crew',
  'Provided Care Supporting Primary EMS Crew',
  'Assumed Primary Care from Another EMS Crew',
  'Incident Support Services Provided (Standby)',
  'Back in Service, No Care / Support Required',
  'Patient Care Transferred to Law Enforcement / ME',
];

export function DispositionDimensionsPanel({ epcr, onUpdate }: Props) {
  const locked = epcr.locked;

  return (
    <section className="border border-slate-700 bg-slate-900/50">
      <header className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-3 py-1.5">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-200">
          Disposition · Four Dimensions (NEMSIS v3.5.1)
        </h3>
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
          Unit · Patient Eval/Care · Crew · Transport
        </span>
      </header>
      <div className="p-3 grid gap-3 md:grid-cols-2">
        {/* eDisposition.27 — Unit Disposition (Mandatory) */}
        <DimensionCard
          icon={<Truck size={13} />}
          label="Unit Disposition"
          element="eDisposition.27"
          usage="Mandatory"
          hint="How this unit's involvement concluded."
          options={UNIT_DISPOSITIONS}
          value={epcr.unit_disposition ?? ''}
          disabled={locked}
          onChange={(v) => onUpdate({ unit_disposition: v || null })}
        />

        {/* eDisposition.28 — Patient Evaluation/Care */}
        <DimensionCard
          icon={<Stethoscope size={13} />}
          label="Patient Evaluation / Care"
          element="eDisposition.28"
          usage="Required"
          hint="Whether the patient was evaluated and whether care was provided."
          options={PATIENT_EVAL_CARE}
          value={epcr.patient_evaluation_care ?? ''}
          disabled={locked}
          onChange={(v) => onUpdate({ patient_evaluation_care: v || null })}
        />

        {/* eDisposition.29 — Crew Disposition */}
        <DimensionCard
          icon={<Users size={13} />}
          label="Crew Disposition"
          element="eDisposition.29"
          usage="Required"
          hint="This crew's role — primary vs assisting vs transferred."
          options={CREW_DISPOSITIONS}
          value={epcr.crew_disposition ?? ''}
          disabled={locked}
          onChange={(v) => onUpdate({ crew_disposition: v || null })}
        />

        {/* eDisposition.30 — Transport Disposition (read-only summary) */}
        <div className={cn('border border-slate-800 bg-slate-900/60 p-2.5')}>
          <div className="flex items-center gap-2">
            <UserCheck size={13} className="text-slate-400" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-300">
              Transport Disposition
            </span>
            <RuleTag
              compact
              sources={[{ standard: 'NEMSIS', element: 'eDisposition.30', usage: 'Required', rationale: 'Transport-decision dimension — captured via the big-button bar below.' }]}
            />
          </div>
          <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
            Set via the primary Transport Decision selector below. Synced to
            eDisposition.30 at export.
          </p>
          <div className="mt-2 text-sm text-slate-100 font-mono">
            {epcr.transport_decision
              ? prettyTransport(epcr.transport_decision)
              : <span className="text-slate-500 italic">Not yet selected</span>}
          </div>
        </div>
      </div>
    </section>
  );
}

interface CardProps {
  icon: React.ReactNode;
  label: string;
  element: string;
  usage: 'Mandatory' | 'Required';
  hint: string;
  options: string[];
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
}

function DimensionCard({
  icon, label, element, usage, hint, options, value, disabled, onChange,
}: CardProps) {
  return (
    <div className={cn(
      'border bg-slate-900/60 p-2.5',
      usage === 'Mandatory' && !value ? 'border-red-900' : 'border-slate-800',
    )}>
      <div className="flex items-center gap-2">
        <span className="text-slate-400">{icon}</span>
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-300">
          {label}
        </span>
        <RuleTag elementId={element} compact />
      </div>
      <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{hint}</p>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="input-field mt-2 text-sm"
      >
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function prettyTransport(decision: string): string {
  switch (decision) {
    case 'transport_this_unit': return 'Transport · this unit';
    case 'transfer_other_unit': return 'Transfer to another unit';
    case 'refusal_care': return 'Refusal · care';
    case 'refusal_transport': return 'Refusal · transport';
    case 'no_patient': return 'No patient';
    case 'treated_released': return 'Treated, released';
    default: return decision;
  }
}
